import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Field, Select } from '@bb/ui';
import type { AppInstanceSummary } from '../../../api/client.ts';
import { problemText } from './problems.ts';
import { pushKeys, useApi } from './usePushSettingsData.ts';

const LANGUAGE_LABEL = { kotlin: 'Kotlin', swift: 'Swift' } as const;

/**
 * The registry as code for one app (BB-77): the Android channels (Kotlin) or iOS categories (Swift) the app registers at
 * startup, generated by the service from the channels and categories above, with a copy button.
 */
export function SdkConfigSnippet({ environmentId, apps }: { environmentId: string; apps: AppInstanceSummary[] }) {
  const api = useApi();
  const [chosen, setChosen] = useState<string | null>(null);
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');
  const appId = chosen && apps.some((a) => a.id === chosen) ? chosen : (apps[0]?.id ?? null);
  const snippet = useQuery({ queryKey: pushKeys.sdkConfig(environmentId, appId ?? ''), queryFn: () => api.pushSdkConfig(environmentId, appId!), enabled: appId !== null });
  if (!appId) return <EmptyState title="No Android or iOS apps" description="The config is generated per app." />;
  const app = apps.find((a) => a.id === appId)!;
  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied('copied');
    } catch {
      setCopied('failed');
    }
  };
  return (
    <div className="push-sdk-config">
      <Field label="App" hint={app.platform === 'android' ? 'Kotlin list of the channels the app creates at startup.' : 'Swift set of the categories the app registers at startup.'}>
        <Select
          options={apps.map((a) => ({ value: a.id, label: `${a.name} (${a.platform === 'ios' ? 'iOS' : 'Android'})` }))}
          value={appId}
          onChange={(e) => {
            setChosen(e.target.value);
            setCopied('idle');
          }}
        />
      </Field>
      {snippet.isError && <ErrorState title="The config could not be generated" message={problemText(snippet.error)} onRetry={() => void snippet.refetch()} />}
      {snippet.isPending && <p aria-busy="true">Generating…</p>}
      {snippet.data && (
        <>
          <div className="actions push-sdk-config__bar">
            <Badge tone="neutral">{LANGUAGE_LABEL[snippet.data.language]}</Badge>
            <Button size="sm" onClick={() => void copy(snippet.data.code)}>
              Copy code
            </Button>
            {copied !== 'idle' && (
              <span role="status" className="hint">
                {copied === 'copied' ? 'Copied' : 'Copy failed — select the code instead'}
              </span>
            )}
          </div>
          <pre className="push-sdk-config__code" aria-label={`${LANGUAGE_LABEL[snippet.data.language]} config for ${app.name}`}>
            <code>{snippet.data.code}</code>
          </pre>
          <p className="hint">Generate it again after changing channels or categories; archived ones are left out.</p>
        </>
      )}
    </div>
  );
}
